'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { ShieldX, HomeIcon } from 'lucide-react'

/**
 * Global API Forbidden Error Handler
 * Listens for 403 errors from API calls and shows full-page access denied
 */
const ApiForbiddenHandler = () => {
  const t = useTranslations()
  const pathname = usePathname()
  const [showForbidden, setShowForbidden] = useState(false)
  const [errorDetails, setErrorDetails] = useState<{ url?: string }>({})
  const [forbiddenPath, setForbiddenPath] = useState<string | null>(null)

  useEffect(() => {
    const handleForbiddenError = (event: CustomEvent) => {
      setErrorDetails({ url: event.detail?.url })
      setForbiddenPath(pathname)
      setShowForbidden(true)
    }

    window.addEventListener('api-forbidden-error', handleForbiddenError as EventListener)

    return () => {
      window.removeEventListener('api-forbidden-error', handleForbiddenError as EventListener)
    }
  }, [pathname])

  // Reset overlay when the user navigates away from the forbidden page
  useEffect(() => {
    if (showForbidden && forbiddenPath !== null && pathname !== forbiddenPath) {
      setShowForbidden(false)
      setForbiddenPath(null)
    }
  }, [pathname, showForbidden, forbiddenPath])

  if (!showForbidden) {
    return null
  }

  return (
    <div className="bg-background fixed inset-0 z-9999 flex min-h-screen items-center px-4 py-12 sm:px-6 md:px-8 lg:px-12 xl:px-16">
      <div className="w-full space-y-6 text-center">
        <div className="space-y-3">
          <div className="mb-4 flex justify-center text-red-500">
            <ShieldX className="h-20 w-20" />
          </div>
          <h1 className="animate-bounce text-4xl font-bold tracking-tighter sm:text-5xl">403</h1>
          <h2 className="mb-5 text-2xl font-semibold tracking-tight sm:text-3xl">{t('access_denied')}</h2>
          <p className="text-gray-500">{t('access_denied_description')}</p>
          {errorDetails.url && <p className="mt-2 text-xs text-gray-400">API: {errorDetails.url}</p>}
        </div>
        <Link
          href="/"
          onClick={() => {
            setShowForbidden(false)
            setForbiddenPath(null)
          }}
          className="inline-flex h-10 items-center rounded-md bg-gray-900 px-8 text-sm font-medium text-gray-50 shadow transition-colors hover:bg-gray-900/90 focus-visible:ring-1 focus-visible:ring-gray-950 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:bg-gray-50 dark:text-gray-900 dark:hover:bg-gray-50/90 dark:focus-visible:ring-gray-300">
          <HomeIcon className="mr-2 h-4 w-4" />
          {t('return_to_home')}
        </Link>
      </div>
    </div>
  )
}

export default ApiForbiddenHandler
